import React from "react";
import { Info, Code2, ShieldCheck, Zap, HeartHandshake } from "lucide-react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";

export function AboutUs() {
  return (
    <div className="max-w-4xl mx-auto py-10 px-4 sm:px-6 lg:px-8">
      <Helmet>
        <title>About Us – 247 DevTools Hub</title>
        <meta name="description" content="Learn about 247 DevTools Hub, our mission to build high-performance, private, browser-based web tools for developers worldwide." />
      </Helmet>

      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 sm:p-10 space-y-8">
        
        {/* Header */}
        <div className="text-center border-b border-slate-100 pb-8">
          <div className="inline-flex p-3 bg-blue-50 text-blue-600 rounded-full mb-4">
            <Info className="w-8 h-8" />
          </div>
          <h1 className="text-3xl font-extrabold text-slate-900 tracking-tight">About 247 DevTools Hub</h1>
          <p className="text-sm text-slate-500 mt-2">Empowering developers with fast, private, single-purpose web utilities.</p>
        </div>

        {/* Mission Pillars */}
        <div className="grid sm:grid-cols-3 gap-6">
          <div className="p-5 bg-slate-50 rounded-xl border border-slate-100 flex flex-col items-center text-center">
            <div className="w-10 h-10 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center mb-3 font-bold">
              <Zap className="w-5 h-5" />
            </div>
            <h3 className="font-bold text-slate-900 text-base mb-1">Instant Performance</h3>
            <p className="text-xs text-slate-500 leading-relaxed">No sluggish server roundtrips or bloat. Tools run instantly in client memory.</p>
          </div>

          <div className="p-5 bg-slate-50 rounded-xl border border-slate-100 flex flex-col items-center text-center">
            <div className="w-10 h-10 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center mb-3 font-bold">
              <ShieldCheck className="w-5 h-5" />
            </div>
            <h3 className="font-bold text-slate-900 text-base mb-1">Privacy First</h3>
            <p className="text-xs text-slate-500 leading-relaxed">Your code, JSON data, keys, and tokens are never saved or sent to any remote backend server.</p>
          </div>

          <div className="p-5 bg-slate-50 rounded-xl border border-slate-100 flex flex-col items-center text-center">
            <div className="w-10 h-10 rounded-full bg-purple-100 text-purple-600 flex items-center justify-center mb-3 font-bold">
              <Code2 className="w-5 h-5" />
            </div>
            <h3 className="font-bold text-slate-900 text-base mb-1">Developer-Centric</h3>
            <p className="text-xs text-slate-500 leading-relaxed">Crafted with precision for web engineers, DevOps admins, and technical creators.</p>
          </div>
        </div>

        {/* Body content */}
        <div className="prose prose-slate max-w-none text-slate-600 space-y-6 text-sm leading-relaxed">
          <section>
            <h2 className="text-xl font-bold text-slate-900 mb-3">Who We Are</h2>
            <p>
              <strong>247 DevTools Hub</strong> was created to solve a common developer annoyance: needing simple, reliable tools (like formatting JSON, parsing JWT tokens, generating hashes, converting cURL commands, or testing regex) without having to navigate ad-bloated websites that secretly send sensitive API keys and payloads to remote backends.
            </p>
            <p className="mt-3">
              We engineered a dedicated platform hosting 50+ specialized developer utilities that run 100% locally in your web browser using client-side JavaScript, WebAssembly, and modern HTML5 APIs.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-slate-900 mb-3">Our Quality & Editorial Standards</h2>
            <p>
              Every utility on 247 DevTools Hub undergoes rigorous testing for mathematical accuracy, edge-case handling, and browser compatibility. Each tool page features:
            </p>
            <ul className="list-disc pl-5 mt-2 space-y-1.5">
              <li><strong>Comprehensive Instructions:</strong> Clear, step-by-step guidance on how to use the tool.</li>
              <li><strong>FAQ Sections:</strong> Detailed answers explaining technical concepts, standards, and best practices.</li>
              <li><strong>Related Developer Tools:</strong> Quick navigation to complementary utilities in the same category.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-bold text-slate-900 mb-3">Transparency & Monetization</h2>
            <p>
              To keep 247 DevTools Hub completely free for developers around the globe, we display non-intrusive advertisements via Google AdSense. We strictly adhere to Google Publisher Policies, ensuring ads never interfere with core tool usability or code copy controls.
            </p>
          </section>

          <section className="bg-blue-50 border border-blue-100 rounded-xl p-6 flex flex-col sm:flex-row items-center justify-between gap-4">
            <div>
              <h3 className="font-bold text-slate-900 text-base mb-1">Have Suggestions or Feature Requests?</h3>
              <p className="text-xs text-slate-600">We continuously update our tools based on community feedback.</p>
            </div>
            <Link 
              to="/contact" 
              className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-medium text-xs px-4 py-2.5 rounded-lg transition-colors shrink-0"
            >
              <HeartHandshake className="w-4 h-4" />
              Contact Our Team
            </Link>
          </section>
        </div>

      </div>
    </div>
  );
}
